import * as React from 'react'
import { FC, useMemo } from 'react'
import addMinutes from 'date-fns/addMinutes'
import { getFormattedAddress } from '@sales-center/common'
import { currencyString } from '@sales-center/common'
import { IExternalEntityServicesModel } from '@user/store/opportunity'
import { ExternalEntityServiceLink } from './ExternalEntityServiceLink'
import { getStyles } from './styling'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { LightTooltip } from '@sales-center/components'
import { SoldServicesStatusTooltip } from '../SoldServicesStatusTooltip/SoldServicesStatusTooltip'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { timeFormat } from '@sales-center/common'
import { Theme, useTheme } from '@mui/material/styles'

interface Props {
  externalEntityService: IExternalEntityServicesModel
}

export const SoldServicesCompletionDetailsCard: FC<Props> = (props) => {
  const {
    externalEntityService: {
      status,
      entityId,
      services,
      createdBy,
      totalValue,
      entityType,
      createdDate,
      locationAddress,
    },
  } = props

  const { formatDate } = useLocalizationDateFormat()
  const tzOffset = new Date(createdDate).getTimezoneOffset()
  const clearDate = addMinutes(new Date(createdDate), tzOffset)

  const createdDateFormatted = useMemo(
    () => formatDate(clearDate) + ' ' + timeFormat(clearDate),
    [clearDate, formatDate],
  )

  const address = useMemo(
    () =>
      getFormattedAddress(
        locationAddress.street1,
        locationAddress.street2,
        locationAddress.city,
        locationAddress.stateAbbreviation,
        locationAddress.postalCode,
      ),
    [locationAddress],
  )

  const formattedValue = useMemo(
    () => `$${currencyString(totalValue)}`,
    [totalValue],
  )
  const styles = getStyles(useTheme())
  return (
    <Card raised variant="elevation" sx={styles.card}>
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Box component="span" sx={styles.title}>
            Service Location:
          </Box>
          <Box component="span">{address}</Box>
        </Box>
        <Box>
          <Box component="span">Total Value:</Box>
          <Box sx={styles.totalValue} component="span">
            {formattedValue}
          </Box>
        </Box>
      </Box>
      <Box>
        <ExternalEntityServiceLink
          externalEntityId={entityId}
          externalEntityType={entityType}
        />
      </Box>
      <Box mt={(theme: Theme) => theme.spacing(1.2)} sx={styles.title}>
        Service
      </Box>
      {services &&
        services.map((service, index) => (
          <Box display="flex" justifyContent="space-between" key={index}>
            <Box>{service.name}</Box>

            {index === services.length - 1 && (
              <Box>
                Status:{' '}
                <LightTooltip
                  slotProps={{ tooltip: { sx: styles.tooltipPopup } }}
                  sx={{
                    ...styles.tooltipHotspot,
                  }}
                  title={<SoldServicesStatusTooltip />}
                >
                  <InfoOutlined
                    sx={styles.icon}
                    data-test-id="soldServicesCompletionStatusInfoIcon"
                  />
                </LightTooltip>
                <Box sx={styles.status} component="span">
                  {status}
                </Box>
                {`by ${createdBy} on
                ${createdDateFormatted}`}
              </Box>
            )}
          </Box>
        ))}
    </Card>
  )
}
